import {
  Tabs,
  Typography,
  Form,
  Input,
  InputNumber,
  Button,
  message,
  Space,
  Spin,
  Upload,
  Modal,
} from 'antd';
import { useState, useEffect } from 'react';
import { useListSetting, useUpdateSetting } from './Hooks';
import FirebaseSettingsTab from './FirebaseSettingsTab';
import PaymentGatewaySettingsTab from './PaymentGatewaySettingsTab';
import { useTranslation } from 'react-i18next';
import { validations } from '../../utils/validation';
import { useFilUpload } from '../../Hooks/Index';
import { getErrorMessage } from '../../utils/errorHandler';
import { useAuthStore } from '../../store/useAuthStore';

const { Title } = Typography;

const SettingsPage = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { user } = useAuthStore();
  const canEditVat = user?.role === 'SUPER_ADMIN';

  const [form] = Form.useForm();
  const [companyForm] = Form.useForm();
  const [vatForm] = Form.useForm();
  const [idleFeeForm] = Form.useForm();
  const [contactDetails, setContactDetails] = useState({
    email: '',
    phone: '',
  });

  const [termsHtml, setTermsHtml] = useState('');
  const [privacyHtml, setPrivacyHtml] = useState('');
  const [faqList, setFaqList] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [companyLogoPath, setCompanyLogoPath] = useState('');
  const [companyAddressLine1, setCompanyAddressLine1] = useState('');
  const [companyAddressLine2, setCompanyAddressLine2] = useState('');
  const [companyTaxNumber, setCompanyTaxNumber] = useState('');
  const [vatRate, setVatRate] = useState(0);
  const [vatLabel, setVatLabel] = useState('KDV');
  const [idleFeeGraceMinutes, setIdleFeeGraceMinutes] = useState(10);
  const [idleFeeRatePerMinute, setIdleFeeRatePerMinute] = useState(0);

  const [logoPreviewVisible, setLogoPreviewVisible] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const { mutateAsync: uploadFile } = useFilUpload(); // S3 upload hook

  const [logoUploading, setLogoUploading] = useState(false);

  const {
    data: settingData,
    isLoading,
    isError,
  } = useListSetting({
    onError: (error) => {
      message.error(getErrorMessage(error, t('common:fetchError')));
    },
  });

  // Add useEffect to handle setting data when it becomes available
  useEffect(() => {
    if (settingData) {
      // Update contact details state with latest data
      const nextContactDetails = settingData.contactDetails || { email: '', phone: '' };
      setContactDetails(nextContactDetails);
      
      const nextTermsHtml = settingData.termsHtml || '';
      const nextPrivacyHtml = settingData.privacyHtml || '';
      setTermsHtml(nextTermsHtml);
      setPrivacyHtml(nextPrivacyHtml);

      // Set company fields individually from settingData
      const nextCompanyName = settingData.companyName || '';
      const nextCompanyPhone = settingData.companyPhone || '';
      const nextCompanyEmail = settingData.companyEmail || '';
      const nextCompanyWebsite = settingData.companyWebsite || '';
      const nextCompanyLogoPath = settingData.companyLogoPath || '';
      const nextCompanyTaxNumber = settingData.companyTaxNumber || '';
      const nextVatRate =
        settingData.vatRate != null ? Number(settingData.vatRate) : 0;
      const nextVatLabel = settingData.vatLabel || 'KDV';
      const nextIdleGrace =
        settingData.idleFeeGraceMinutes != null
          ? Number(settingData.idleFeeGraceMinutes)
          : 10;
      const nextIdleRate =
        settingData.idleFeeRatePerMinute != null
          ? Number(settingData.idleFeeRatePerMinute)
          : 0;

      // Parse address into line1 and line2 using a unique delimiter
      let nextCompanyAddressLine1 = '';
      let nextCompanyAddressLine2 = '';
      if (settingData.companyAddress) {
        // Use a unique delimiter for splitting
        const [line1 = '', line2 = ''] = settingData.companyAddress.split('|||');
        nextCompanyAddressLine1 = line1;
        nextCompanyAddressLine2 = line2;
      }

      setCompanyName(nextCompanyName);
      setCompanyPhone(nextCompanyPhone);
      setCompanyEmail(nextCompanyEmail);
      setCompanyWebsite(nextCompanyWebsite);
      setCompanyLogoPath(nextCompanyLogoPath);
      setCompanyTaxNumber(nextCompanyTaxNumber);
      setCompanyAddressLine1(nextCompanyAddressLine1);
      setCompanyAddressLine2(nextCompanyAddressLine2);
      setVatRate(nextVatRate);
      setVatLabel(nextVatLabel);
      setIdleFeeGraceMinutes(nextIdleGrace);
      setIdleFeeRatePerMinute(nextIdleRate);

      // Handle FAQ data format - ensure we're using the right property names
      const formattedFaqList = (settingData.faqList || []).map((faq) => ({
        question: faq.questions || '',
        answer: faq.answers || '',
      }));

      setFaqList(formattedFaqList);

      // Update form fields with latest data
      form.setFieldsValue(nextContactDetails);
      companyForm.setFieldsValue({
        name: nextCompanyName,
        addressLine1: nextCompanyAddressLine1,
        addressLine2: nextCompanyAddressLine2,
        phone: nextCompanyPhone,
        email: nextCompanyEmail,
        website: nextCompanyWebsite,
        logoPath: nextCompanyLogoPath,
        taxNumber: nextCompanyTaxNumber,
      });
      vatForm.setFieldsValue({
        vatRate: nextVatRate,
        vatLabel: nextVatLabel,
      });
      idleFeeForm.setFieldsValue({
        idleFeeGraceMinutes: nextIdleGrace,
        idleFeeRatePerMinute: nextIdleRate,
      });
    }
  }, [settingData, form, companyForm, vatForm, idleFeeForm]);

  const updateSettingMutation = useUpdateSetting({
    onSuccess: () => {
      message.success(t('common:updateSuccess')); // Replace hardcoded success messages with translations
    },
    onError: (error) => {
      console.error('Error updating settings:', error);
      message.error(getErrorMessage(error, t('common:updateError')));
    },
  });

  const handleUpdateSettings = (updatedFields) => {
    // Sanitize contactDetails by removing the _id field
    const sanitizedContactDetails = { ...contactDetails };
    delete sanitizedContactDetails._id;

    // Combine all settings data into a single payload
    const payload = {
      contactDetails: sanitizedContactDetails,
      termsHtml,
      privacyHtml,
      faqList: faqList.map((faq) => ({
        questions: faq.question,
        answers: faq.answer,
      })),
      ...(companyTaxNumber && { companyTaxNumber }),
      vatRate,
      vatLabel,
      idleFeeGraceMinutes,
      idleFeeRatePerMinute,
      ...updatedFields,
    };

    updateSettingMutation.mutate(payload);
  };

  const handleContactSave = (values) => {
    setContactDetails(values);
    handleUpdateSettings({ contactDetails: values });
  };

  const handleTermsSave = () => {
    handleUpdateSettings({ termsHtml });
  };

  const handlePrivacySave = () => {
    handleUpdateSettings({ privacyHtml });
  };

  const handleFaqSave = () => {
    const formattedFaqs = faqList
      .filter((faq) => faq.question.trim() && faq.answer.trim()) // Remove empty FAQs
      .map((faq) => ({
        questions: faq.question,
        answers: faq.answer,
      }));

    handleUpdateSettings({ faqList: formattedFaqs });
  };

  // Helper to update company address state
  const updateCompanyAddressState = (addressLine1, addressLine2) => {
    setCompanyAddressLine1(addressLine1 || '');
    setCompanyAddressLine2(addressLine2 || '');
  };

  // Helper to update company info state
  const updateCompanyInfoState = (values) => {
    setCompanyName(values.name);
    setCompanyPhone(values.phone);
    setCompanyEmail(values.email);
    setCompanyWebsite(values.website);
    setCompanyLogoPath(values.logoPath);
    setCompanyTaxNumber(values.taxNumber);
  };

  const handleCompanyDetailsSave = (values) => {
    updateCompanyInfoState(values);
    updateCompanyAddressState(values.addressLine1, values.addressLine2);

    // Combine address lines for API using a unique delimiter
    const combinedAddress = `${values.addressLine1 || ''}|||${values.addressLine2 || ''}`;

    const payload = {
      companyName: values.name,
      companyAddress: combinedAddress,
      companyPhone: values.phone,
      companyEmail: values.email,
      companyWebsite: values.website,
      companyLogoPath: values.logoPath,
    };

    // Only add companyTaxNumber if it has a value
    if (values.taxNumber?.trim()) {
      payload.companyTaxNumber = values.taxNumber;
    }

    handleUpdateSettings(payload);
  };

  const handleVatSave = (values) => {
    const nextRate = Number(values.vatRate) || 0;
    const nextLabel = (values.vatLabel || 'KDV').trim() || 'KDV';
    setVatRate(nextRate);
    setVatLabel(nextLabel);
    handleUpdateSettings({
      vatRate: nextRate,
      vatLabel: nextLabel,
    });
  };

  const handleIdleFeeSave = (values) => {
    const nextGrace = Number(values.idleFeeGraceMinutes);
    const nextRate = Number(values.idleFeeRatePerMinute);
    const grace = Number.isFinite(nextGrace) ? Math.max(0, nextGrace) : 10;
    const rate = Number.isFinite(nextRate) ? Math.max(0, nextRate) : 0;
    setIdleFeeGraceMinutes(grace);
    setIdleFeeRatePerMinute(rate);
    handleUpdateSettings({
      idleFeeGraceMinutes: grace,
      idleFeeRatePerMinute: rate,
    });
  };

  const addFaq = () => {
    setFaqList([...faqList, { question: '', answer: '' }]);
  };

  const updateFaq = (index, field, value) => {
    const updatedFaqList = [...faqList];
    updatedFaqList[index][field] = value;
    setFaqList(updatedFaqList);
  };

  const deleteFaq = (index) => {
    const updatedFaqList = faqList.filter((_, i) => i !== index);
    setFaqList(updatedFaqList);
  };

  const handleLogoPreview = () => {
    setLogoPreviewUrl(companyLogoPath);
    setLogoPreviewVisible(true);
  };

  const handleLogoUpload = async ({ file, onSuccess, onError }) => {
    setLogoUploading(true);
    try {
      const result = await uploadFile(file);
      const url = result.Location || result.url || '';
      setCompanyLogoPath(url);
      companyForm.setFieldsValue({ logoPath: url });
      handleUpdateSettings({ companyLogoPath: url });
      setLogoUploading(false);
      onSuccess(result, file);
    } catch (err) {
      setLogoUploading(false);
      onError(err);
      message.error(getErrorMessage(err, t('common:updateError')));
    }
  };

  const logoUploadProps = {
    accept: '.png,.jpg,.jpeg,.svg',
    showUploadList: false,
    customRequest: handleLogoUpload,
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="small" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex justify-center items-center h-64">
        <Typography.Text type="danger">
          {t('common:fetchError')} 
        </Typography.Text>
      </div>
    );
  }

  // Helper to render FAQ form items with unique keys
  const renderFaqList = (faqList, updateFaq, deleteFaq, t) =>
    faqList.map((faq, index) => {
      // Use a unique key if possible, fallback to question+answer+index
      const key = faq.question + faq.answer + index;
      return (
        <div key={key} className="mb-4 p-4 rounded bg-gray-50">
          <Form layout="vertical">
            <Form.Item
              label={`${t('faqQuestion')} ${index + 1}`}
              rules={[{ required: true, message: t('questionRequired') }]}
            >
              <Input
                value={faq.question}
                onChange={(e) => updateFaq(index, 'question', e.target.value)}
                placeholder={t('questionPlaceholder')}
              />
            </Form.Item>
            <Form.Item
              label={`${t('faqAnswer')} ${index + 1}`}
              rules={[{ required: true, message: t('answerRequired') }]}
            >
              <Input.TextArea
                rows={3}
                value={faq.answer}
                onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                placeholder={t('answerPlaceholder')}
              />
            </Form.Item>
            <Space>
              <Button danger onClick={() => deleteFaq(index)}>
                {t('deleteButton')}
              </Button>
            </Space>
          </Form>
        </div>
      );
    });

  // Refactor: extract FAQ tab content to reduce cognitive complexity
  const renderFaqTab = () => (
    <div className="max-h-[74vh] overflow-y-scroll pr-4">
      {renderFaqList(faqList, updateFaq, deleteFaq, t)}
      <div className="flex flex-row gap-4 sticky bottom-0 bg-white p-2 ">
        <Button onClick={addFaq} className="mt-4">
          {t('addFaqButton')}
        </Button>
        <Button
          type="primary"
          className="mt-4"
          onClick={handleFaqSave}
          loading={updateSettingMutation.isPending}
        >
          {t('saveFaqButton')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center mb-4 text-xs">
        {t('settingsPageDescription')}
      </div>
      <Tabs 
        defaultActiveKey="1" 
        className="flex-1 settings-tabs min-w-0 [&_.ant-tabs-nav]:mb-0 [&_.ant-tabs-nav-wrap]:overflow-x-auto [&_.ant-tabs-nav-list]:flex-nowrap"
        tabBarGutter={24}
        tabBarStyle={{ marginBottom: 0 }}
        items={[
          {
            key: '1',
            label: t('contactDetailsTab'),
            children: (
              <div className="h-full mt-4 overflow-y-auto pr-4">
                <Form
                  form={form}
                  layout="vertical"
                  key={`contact-form-${contactDetails.email}-${contactDetails.phone}`}
                  initialValues={contactDetails}
                  onFinish={handleContactSave}
                >
                  <Form.Item
                    label={t('emailLabel')}
                    name="email"
                    rules={[
                      { required: true, message: t('emailRequired') },
                      { type: 'email', message: t('invalidEmail') },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    label={t('phoneLabel')}
                    name="phone"
                    rules={[
                      { required: true, message: t('phoneRequired') },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={updateSettingMutation.isPending}
                    >
                      {t('saveButton')}
                    </Button>
                  </Form.Item>
                </Form>
              </div>
            ),
          },
          {
            key: '2',
            label: t('termsPrivacyTab'),
            children: (
              <div className="h-full mt-4 overflow-y-auto pr-4">
                <Title level={4}>{t('termsTitle')}</Title>
                <textarea
                  className="w-full p-2 border rounded"
                  rows="10"
                  value={termsHtml}
                  onChange={(e) => setTermsHtml(e.target.value)}
                ></textarea>
                <Button
                  type="primary"
                  className="mt-4"
                  onClick={handleTermsSave}
                  loading={updateSettingMutation.isPending}
                >
                  {t('saveTermsButton')}
                </Button>
                <Title level={4} className="mt-6">
                  {t('privacyTitle')}
                </Title>
                <textarea
                  className="w-full p-2 border rounded"
                  rows="10"
                  value={privacyHtml}
                  onChange={(e) => setPrivacyHtml(e.target.value)}
                ></textarea>
                <Button
                  type="primary"
                  className="mt-4"
                  onClick={handlePrivacySave}
                  loading={updateSettingMutation.isPending}
                >
                  {t('savePrivacyButton')}
                </Button>
              </div>
            ),
          },
          {
            key: '3',
            label: t('faqTab'),
            children: (
              <div className="h-full mt-4">
                {renderFaqTab()}
              </div>
            ),
          },
          {
            key: '4',
            label: t('companyDetailsTab'),
            children: (
              <div className="h-full mt-4 overflow-y-auto pr-4">
                <Form
                  form={companyForm}
                  layout="vertical"
                  initialValues={{
                    name: companyName,
                    addressLine1: companyAddressLine1,
                    addressLine2: companyAddressLine2,
                    phone: companyPhone,
                    email: companyEmail,
                    website: companyWebsite,
                    logoPath: companyLogoPath,
                    taxNumber: companyTaxNumber,
                  }}
                  onFinish={handleCompanyDetailsSave}
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Form.Item
                        label={t('companyNameLabel')}
                        name="name"
                        rules={[
                          validations.maxLengthWithTrim(100, t('maxLengthError', { max: 100 })),
                          validations.noSpaces(t('noSpacesAllowed')),
                        ]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        label={t('companyAddressLine1Label')}
                        name="addressLine1"
                        rules={[
                          validations.maxLengthWithTrim(100, t('maxLengthError', { max: 100 })),
                          validations.noSpaces(t('noSpacesAllowed')),
                        ]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        label={t('companyAddressLine2Label')}
                        name="addressLine2"
                        rules={[
                          validations.maxLengthWithTrim(100, t('maxLengthError', { max: 100 })),
                          validations.noSpaces(t('noSpacesAllowed')),
                        ]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        label={t('companyPhoneLabel')}
                        name="phone"
                        rules={[
                          { required: true, message: t('phoneRequired') },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </div>
                    <div>
                      <Form.Item
                        label={t('companyEmailLabel')}
                        name="email"
                        rules={[
                          validations.email(t('invalidEmail')),
                          validations.noSpaces(t('noSpacesAllowed')),
                        ]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        label={t('companyWebsiteLabel')}
                        name="website"
                        rules={[
                          {
                            type: 'url',
                            message: t('invalidWebsite'),
                          },
                          validations.noSpaces(t('noSpacesAllowed')),
                        ]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        label={t('companyTaxNumberLabel')}
                        name="taxNumber"
                        required={false}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        label={t('companyLogoPathLabel')}
                        name="logoPath"
                        rules={[
                          validations.maxLengthWithTrim(500, t('maxLengthError', { max: 500 })),
                          validations.noSpaces(t('noSpacesAllowed')),
                        ]}
                      >
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {companyLogoPath && (
                              <>
                                <img
                                  src={companyLogoPath}
                                  alt="logo"
                                  style={{ width: 80, height: 80, objectFit: 'contain', border: '1px solid #eee', borderRadius: 4 }}
                                />
                                <Button
                                  onClick={handleLogoPreview}
                                  style={{ marginLeft: 8 }}
                                  type="default"
                                >
                                  {t('common:preview') || 'Preview'}
                                </Button>
                              </>
                            )}
                            <Upload
                              {...logoUploadProps}
                              showUploadList={false}
                              style={{ marginLeft: 8 }}
                              listType="picture"
                              disabled={logoUploading}
                            >
                              <Button type="primary" loading={logoUploading}>
                                {t('common:replace') || 'Replace'}
                              </Button>
                            </Upload>
                          </div>
                          <Modal
                            open={logoPreviewVisible}
                            footer={null}
                            onCancel={() => setLogoPreviewVisible(false)}
                          >
                            <img alt="logo preview" style={{ width: '100%' }} src={logoPreviewUrl} />
                          </Modal>
                          <Input
                            style={{ marginTop: 8 }}
                            value={companyLogoPath}
                            readOnly
                            placeholder="Logo URL"
                          />
                        </>
                      </Form.Item>
                    </div>
                  </div>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={updateSettingMutation.isPending}
                    >
                      {t('saveButton')}
                    </Button>
                  </Form.Item>
                </Form>
              </div>
            ),
          },
          {
            key: '5',
            label: t('vatTab'),
            children: (
              <div className="h-full mt-4 overflow-y-auto pr-4">
                <Title level={4}>{t('vatTitle')}</Title>
                <p className="text-xs mb-4 opacity-70">{t('vatDescription')}</p>
                <Form
                  form={vatForm}
                  layout="vertical"
                  initialValues={{ vatRate, vatLabel }}
                  onFinish={handleVatSave}
                  disabled={!canEditVat}
                >
                  <Form.Item
                    label={t('vatRateLabel')}
                    name="vatRate"
                    rules={[
                      { required: true, message: t('vatRateRequired') },
                      {
                        type: 'number',
                        min: 0,
                        max: 100,
                        message: t('vatRateRange'),
                      },
                    ]}
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      step={0.01}
                      precision={2}
                      addonAfter="%"
                      className="w-full"
                    />
                  </Form.Item>
                  <Form.Item
                    label={t('vatLabelLabel')}
                    name="vatLabel"
                    rules={[
                      validations.maxLengthWithTrim(50, t('maxLengthError', { max: 50 })),
                    ]}
                  >
                    <Input placeholder="KDV" />
                  </Form.Item>
                  {canEditVat && (
                    <Form.Item>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={updateSettingMutation.isPending}
                      >
                        {t('saveButton')}
                      </Button>
                    </Form.Item>
                  )}
                </Form>
              </div>
            ),
          },
          {
            key: '5b',
            label: t('idleFeeTab'),
            children: (
              <div className="h-full mt-4 overflow-y-auto pr-4">
                <Title level={4}>{t('idleFeeTitle')}</Title>
                <p className="text-xs mb-4 opacity-70">{t('idleFeeDescription')}</p>
                <Form
                  form={idleFeeForm}
                  layout="vertical"
                  initialValues={{
                    idleFeeGraceMinutes,
                    idleFeeRatePerMinute,
                  }}
                  onFinish={handleIdleFeeSave}
                  disabled={!canEditVat}
                >
                  <Form.Item
                    label={t('idleFeeGraceLabel')}
                    name="idleFeeGraceMinutes"
                    rules={[
                      { required: true, message: t('idleFeeGraceRequired') },
                      {
                        type: 'number',
                        min: 0,
                        message: t('idleFeeGraceMin'),
                      },
                    ]}
                  >
                    <InputNumber
                      min={0}
                      max={24 * 60}
                      step={1}
                      precision={0}
                      className="w-full"
                    />
                  </Form.Item>
                  <Form.Item
                    label={t('idleFeeRateLabel')}
                    name="idleFeeRatePerMinute"
                    rules={[
                      { required: true, message: t('idleFeeRateRequired') },
                      {
                        type: 'number',
                        min: 0,
                        message: t('idleFeeRateMin'),
                      },
                    ]}
                  >
                    <InputNumber
                      min={0}
                      step={0.01}
                      precision={2}
                      className="w-full"
                    />
                  </Form.Item>
                  {canEditVat && (
                    <Form.Item>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={updateSettingMutation.isPending}
                      >
                        {t('saveButton')}
                      </Button>
                    </Form.Item>
                  )}
                </Form>
              </div>
            ),
          },
          {
            key: '6',
            label: t('firebaseTab'),
            children: (
              <div className="h-full overflow-y-auto">
                <FirebaseSettingsTab firebaseMeta={settingData?.firebase || {}} />
              </div>
            ),
          },
          {
            key: '7',
            label: t('paymentGatewayTab', { defaultValue: 'Payment Gateway' }),
            children: <PaymentGatewaySettingsTab />,
          },
        ]}
      />
    </div>
  );
};

export default SettingsPage;
